// src/pages/memberManagement/memberList/memberList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { Button, DatePicker, Modal, Pagination, Select, Table, Tooltip } from "antd";
import { FaCheck, FaEye, FaTrash, FaTimes } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { initialRoles } from "../../enabledRoles/enabledRoles";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import AppBadge from "../../../components/badge/badge";
import "./memberList.css";

const { RangePicker } = DatePicker;

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  users: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: null,
  showDateRangeModal: false,
  tempFromDate: null,
  tempToDate: null,
  fromDate: null,
  toDate: null,
  showApproveModal: false,
  selectedMemberId: null,
  isApproving: false
};

// Reducer for state management
const memberReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setUsers":
      return { ...state, users: action.payload };
    case "updateUserStatus":
      return {
        ...state,
        users: state.users.map((user) => (user.id === action.payload.id ? { ...user, status: action.payload.status } : user))
      };
    case "updateUserRole":
      return {
        ...state,
        users: state.users.map((user) => (user.id === action.payload.id ? { ...user, role: action.payload.role } : user))
      };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload, currentPage: 1 };
    case "setDateRangeModal":
      return { ...state, showDateRangeModal: action.payload };
    case "setTempDateRange":
      return {
        ...state,
        tempFromDate: action.payload.from,
        tempToDate: action.payload.to
      };
    case "setDateRange":
      return {
        ...state,
        fromDate: action.payload.from,
        toDate: action.payload.to
      };
    case "setApproveModal":
      return {
        ...state,
        showApproveModal: action.payload.show,
        selectedMemberId: action.payload.memberId || null
      };
    case "setIsApproving":
      return { ...state, isApproving: action.payload };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1,
        fromDate: null,
        toDate: null,
        tempFromDate: null,
        tempToDate: null,
        showDateRangeModal: false
      };
    default:
      return state;
  }
};

function MemberList() {
  const { notification, showNotification, hideNotification } = useNotification();
  // Per-user unread notification state
  const [userUnreadActivityCounts, setUserUnreadActivityCounts] = useState({});
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(memberReducer, initialState);

  // Additional state for role assignment
  const [roleAssignLoading, setRoleAssignLoading] = useState({});

  // Tracks which flagged rows the user has manually unpinned
  const [unpinnedRows, setUnpinnedRows] = useState(new Set());

  // State for immediate search display
  const [displaySearchTerm, setDisplaySearchTerm] = useState("");

  const {
    isLoading,
    searchTerm,
    filters,
    users,
    currentPage,
    pageSize,
    sortConfig,
    showDateRangeModal,
    tempFromDate,
    tempToDate,
    fromDate,
    toDate,
    showApproveModal,
    selectedMemberId,
    isApproving
  } = state;

  const availableRoles = initialRoles;

  // Function to fetch unread counts for all users
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const logsRes = await axios.get(API_ENDPOINTS.MEMBER_UPDATES, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allLogs = logsRes.data.items || [];
      // Filter only unread logs
      const unreadLogs = allLogs.filter((log) => log.isUnread === true);

      // Group by userId and count
      const countsObj = {};
      unreadLogs.forEach((log) => {
        const userId = log.userId;
        if (userId) {
          countsObj[userId] = (countsObj[userId] || 0) + 1;
        }
      });

      setUserUnreadActivityCounts(countsObj);
      console.log("[MemberList] Unread counts per user:", countsObj);
    } catch (error) {
      console.error("[MemberList] Error fetching unread counts:", error);
      // Don't show error to user, just set empty counts
      setUserUnreadActivityCounts({});
    }
  }, []);

  // Refresh unread counts when page gets focus (user returns from member details)
  const refreshUnreadCounts = useCallback(() => {
    fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      registrationDate: {
        label: "Registration Date",
        options: [
          { value: "newest", label: "Newest" },
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "oldest", label: "Oldest" },
          { value: "dateRange", label: "Custom Date Range" }
        ]
      },
      roleType: {
        label: "Role Type",
        options: [
          { value: "users", label: "Users" },
          { value: "members", label: "Members" }
        ]
      },
      approvalStatus: {
        label: "Approval Status",
        options: [
          { value: "approved", label: "Approved" },
          { value: "notApproved", label: "Not Approved" }
        ]
      },
      activityStatus: {
        label: "Activity Status",
        options: [
          { value: "newUser", label: "New User" },
          { value: "activeUser", label: "Active User" },
          { value: "inactiveUser", label: "Inactive User" },
          { value: "formerUser", label: "Former User" }
        ]
      }
    }),
    []
  );

  // Date filtering utility (newest/oldest from stash + existing range filters)
  const filterByDate = useCallback(
    (items, dateFilter) => {
      if (!dateFilter) return items;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      switch (dateFilter) {
        case "newest":
          return [...items].sort((a, b) => new Date(b.registrationDate) - new Date(a.registrationDate));
        case "oldest":
          return [...items].sort((a, b) => new Date(a.registrationDate) - new Date(b.registrationDate));
        case "last7Days": {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(today.getDate() - 7);
          return items.filter((item) => new Date(item.registrationDate) >= sevenDaysAgo);
        }
        case "last30Days": {
          const thirtyDaysAgo = new Date(today);
          thirtyDaysAgo.setDate(today.getDate() - 30);
          return items.filter((item) => new Date(item.registrationDate) >= thirtyDaysAgo);
        }
        case "dateRange": {
          if (fromDate && toDate) {
            const from = new Date(fromDate);
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            return items.filter((item) => {
              const date = new Date(item.registrationDate);
              return date >= from && date <= to;
            });
          }
          return items;
        }
        default:
          return items;
      }
    },
    [fromDate, toDate]
  );

  // Global search function
  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [
        item.id?.toString() || "",
        item.fullName || "",
        item.phoneNumber || "",
        item.role || "",
        item.status || "",
        item.applicationId?.toString() || "",
        item.email?.toString() || "",
        item.registrationDate
          ? new Date(item.registrationDate)
              .toLocaleDateString("en-GB", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "short",
                year: "numeric"
              })
              .replace(/ /g, "-")
          : ""
      ];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  // Apply all filters, search, and sorting in correct order
  const filteredData = useMemo(() => {
    let result = [...users];

    // Step 1: Apply date filter (includes sorting for newest/oldest)
    result = filterByDate(result, filters.registrationDate);

    // Step 2: Apply role type filter
    if (filters.roleType) {
      if (filters.roleType === "users") {
        result = result.filter((user) => user.role?.toLowerCase() === "user");
      } else if (filters.roleType === "members") {
        result = result.filter((user) => {
          const role = user.role?.toLowerCase() || "";
          return role !== "user" && role !== "no role" && role !== "-" && role !== "";
        });
      }
    }

    // Step 3: Apply activity status filter
    if (filters.activityStatus) {
      switch (filters.activityStatus) {
        case "newUser":
          result = result.filter((u) => u.isNew);
          break;
        case "activeUser":
          result = result.filter((u) => u.isActive);
          break;
        case "inactiveUser":
          result = result.filter((u) => !u.isActive);
          break;
        case "formerUser":
          result = result.filter((u) => u.isFormer);
          break;
        default:
          break;
      }
    }

    // Step 4: Apply global search
    result = globalSearch(result, searchTerm);

    // Step 5: Apply sorting BEFORE pagination (if not already sorted by date)
    if (sortConfig && filters.registrationDate !== "newest" && filters.registrationDate !== "oldest") {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;

        if (key === "registrationDate") {
          const dateA = new Date(a.registrationDate);
          const dateB = new Date(b.registrationDate);
          return direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";

        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [users, filters, searchTerm, filterByDate, globalSearch, sortConfig]);

  // Helper: a row is "flagged" if it has a non-user role but is not admin-approved
  const isFlaggedRow = useCallback(
    (user) => {
      const role = user.role?.toLowerCase() || "";
      const isApproved = user.isAdminApproved === true || user.status === "Approved";
      const hasElevatedRole = role !== "" && role !== "user" && role !== "no role";
      return hasElevatedRole && !isApproved;
    },
    []
  );

  // Get current page data (pagination happens AFTER all processing)
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageSlice = filteredData.slice(startIndex, endIndex);

    // Separate pinned (flagged + not unpinned) from the rest
    const pinned = pageSlice.filter((u) => isFlaggedRow(u) && !unpinnedRows.has(u.id));
    const rest = pageSlice.filter((u) => !isFlaggedRow(u) || unpinnedRows.has(u.id));

    return [...pinned, ...rest];
  }, [filteredData, currentPage, pageSize, isFlaggedRow, unpinnedRows]);

  // Event handlers
  const debouncedDispatch = useMemo(
    () =>
      debounce((term) => {
        dispatch({ type: "setSearchTerm", payload: term });
      }, 300),
    []
  );

  const handleSearch = useCallback(
    (term) => {
      setDisplaySearchTerm(term);
      debouncedDispatch(term);
    },
    [debouncedDispatch]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (newFilters.registrationDate === "dateRange" && filters.registrationDate !== "dateRange") {
        dispatch({ type: "setDateRangeModal", payload: true });
        return;
      }

      if (filters.registrationDate === "dateRange" && newFilters.registrationDate !== "dateRange") {
        dispatch({
          type: "setDateRange",
          payload: { from: null, to: null }
        });
        dispatch({
          type: "setTempDateRange",
          payload: { from: null, to: null }
        });
      }

      dispatch({ type: "setFilters", payload: newFilters });
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
    setDisplaySearchTerm("");
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const openApproveModal = useCallback((memberId, e) => {
    e.stopPropagation();
    dispatch({ type: "setApproveModal", payload: { show: true, memberId } });
  }, []);

  // Confirm approve function
  const confirmApprove = useCallback(async () => {
    if (!selectedMemberId) return;
    dispatch({ type: "setIsApproving", payload: true });

    const currentMember = users.find((u) => u.id === selectedMemberId);
    const newStatus = currentMember?.status === "Approved" ? "Pending" : "Approved";

    try {
      const token = await getAuthToken();

      const response = await axios.put(
        `${API_ENDPOINTS.MEMBERS}/${selectedMemberId}`,
        { isAdminApproved: newStatus === "Approved" },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.status === 200) {
        dispatch({
          type: "updateUserStatus",
          payload: { id: selectedMemberId, status: newStatus }
        });

        showNotification(newStatus === "Approved" ? "Member approved successfully!" : "Member approval removed successfully!", newStatus === "Approved" ? "success" : "warning");

        dispatch({ type: "setApproveModal", payload: { show: false } });
      } else {
        showNotification(response.data?.message || "Failed to update member status.", "error");
      }
    } catch (error) {
      console.error("Approval error:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Failed to update member status.", "error");

      // If the API call failed, refetch to ensure data consistency
      try {
        const token = await getAuthToken();
        const res = await axios.get(API_ENDPOINTS.MEMBERS, {
          headers: { Authorization: `Bearer ${token}` }
        });
        dispatch({ type: "setUsers", payload: res.data.users || [] });
      } catch (refreshError) {
        console.error("Error refreshing members after failed update:", refreshError);
      }
    } finally {
      dispatch({ type: "setIsApproving", payload: false });
    }
  }, [selectedMemberId, users, showNotification]);

  const handleRoleAssign = useCallback(
    async (memberId, newGroupName) => {
      setRoleAssignLoading((prev) => ({ ...prev, [memberId]: true }));

      try {
        const member = users.find((u) => u.id === memberId);
        const currentGroupName = member.role;
        const token = await getAuthToken();

        // If selecting the same role, do nothing
        if (currentGroupName === newGroupName) {
          showNotification(`Member already has "${newGroupName}" role`, "info");
          setRoleAssignLoading((prev) => ({ ...prev, [memberId]: false }));
          return;
        }

        // Remove current role if exists
        if (currentGroupName && currentGroupName !== "" && currentGroupName !== "user") {
          await axios.post(
            API_ENDPOINTS.USER_ROLES,
            {
              action: "remove",
              username: memberId,
              groupName: currentGroupName
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              }
            }
          );
        }

        // Add new role if selected
        if (newGroupName && newGroupName !== "") {
          await axios.post(
            API_ENDPOINTS.USER_ROLES,
            {
              action: "add",
              username: memberId,
              groupName: newGroupName
            },
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              }
            }
          );
        }

        // Success message
        let message = "";
        if (currentGroupName && newGroupName) {
          message = `Role changed from "${currentGroupName}" to "${newGroupName}" successfully!`;
        } else if (newGroupName) {
          message = `Role "${newGroupName}" assigned successfully!`;
        } else if (currentGroupName) {
          message = `Role "${currentGroupName}" removed successfully!`;
        } else {
          message = `Role updated successfully!`;
        }

        showNotification(message, "success");

        // Update local state immediately (optimistic update)
        dispatch({
          type: "updateUserRole",
          payload: { id: memberId, role: newGroupName }
        });
      } catch (error) {
        console.error("Role assignment error:", error);
        showNotification("Failed to update role: " + (error.response?.data?.body || error.message), "error");

        // If the API call failed, refetch to ensure data consistency
        const token = await getAuthToken();
        axios
          .get(API_ENDPOINTS.MEMBERS, {
            headers: { Authorization: `Bearer ${token}` }
          })
          .then((res) => {
            dispatch({ type: "setUsers", payload: res.data.users || [] });
          })
          .catch((refreshError) => {
            console.error("Error refreshing members after failed update:", refreshError);
          });
      } finally {
        setRoleAssignLoading((prev) => ({ ...prev, [memberId]: false }));
      }
    },
    [users, showNotification]
  );

  const handleDateRangeChange = useCallback((dates, dateStrings) => {
    dispatch({
      type: "setTempDateRange",
      payload: {
        from: dateStrings[0] || null,
        to: dateStrings[1] || null
      }
    });
  }, []);

  const applyDateRangeFilter = useCallback(() => {
    if (tempFromDate && tempToDate) {
      dispatch({
        type: "setDateRange",
        payload: { from: tempFromDate, to: tempToDate }
      });
      dispatch({
        type: "setFilters",
        payload: { ...filters, registrationDate: "dateRange" }
      });
      dispatch({ type: "setDateRangeModal", payload: false });
    } else {
      alert("Please select both from and to dates");
    }
  }, [tempFromDate, tempToDate, filters]);

  const handleDateRangeModalCancel = useCallback(() => {
    dispatch({ type: "setDateRangeModal", payload: false });
    dispatch({
      type: "setTempDateRange",
      payload: { from: null, to: null }
    });
    // Clear the registration date filter
    const updatedFilters = { ...filters };
    delete updatedFilters.registrationDate;
    dispatch({ type: "setFilters", payload: updatedFilters });
  }, [filters]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    if (sorter.order) {
      dispatch({
        type: "setSortConfig",
        payload: {
          key: sorter.field,
          direction: sorter.order === "ascend" ? "asc" : "desc"
        }
      });
    } else {
      dispatch({
        type: "setSortConfig",
        payload: null
      });
    }
  }, []);

  const prepareExcelData = useCallback(() => {
    return filteredData.map((user) => ({
      "Membership Form ID": user.applicationId ?? user.id,
      "Full Name": user.fullName,
      "Phone Number": user.phoneNumber,
      "Registration Date": user.registrationDate,
      Role: user.role,
      "Approval Status": user.status === "Approved" ? "Approved" : "Not Approved",
      Status: user.status || "-"
    }));
  }, [filteredData]);

  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "MEMBERSHIP FORM ID",
        dataIndex: "id",
        render: (_, record) => {
          const displayId = record.applicationId ?? record.id;
          const userId = record.userId || record.id;
          const unreadCount = userUnreadActivityCounts[userId] || 0;
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>{displayId ?? "-"}</span>
              {unreadCount > 0 && (
                <AppBadge
                  count={unreadCount}
                  title={`${unreadCount} unread activity log${unreadCount > 1 ? "s" : ""} for this user`}
                />
              )}
            </div>
          );
        }
      },
      {
        title: "FULL NAME",
        dataIndex: "fullName",
        sorter: true,
        sortOrder: sortConfig?.key === "fullName" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => (
          <div className="mrl-name-email">
            <div className="mrl-name">{record.fullName || "-"}</div>
          </div>
        )
      },
      {
        title: "PHONE NUMBER",
        dataIndex: "phoneNumber",
        render: (phone) => phone || "-"
      },
      {
        title: "REGISTRATION DATE",
        dataIndex: "registrationDate",
        sorter: true,
        sortOrder: sortConfig?.key === "registrationDate" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "ROLE",
        dataIndex: "role",
        sorter: true,
        sortOrder: sortConfig?.key === "role" ? (sortConfig.direction === "asc" ? "ascend" : "descend") : null,
        render: (_, record) => {
          const isApproved = record.isAdminApproved === true || record.status === "Approved";
          return (
            <Tooltip title={!isApproved ? "Approve the membership form to assign a role" : ""}>
              <Select
                value={record.role || undefined}
                onChange={(value) => handleRoleAssign(record.id, value)}
                style={{ width: "12em", height: "1.8em" }}
                placeholder="Select Role"
                options={[{ value: "", label: "No Role" }, ...availableRoles.map((r) => ({ value: r.type, label: r.type }))]}
                loading={roleAssignLoading[record.id]}
                disabled={roleAssignLoading[record.id] || !isApproved}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "ACTIONS",
        render: (_, record) => {
          const isPinned = isFlaggedRow(record) && !unpinnedRows.has(record.id);
          return (
            <div className="mrl-action-buttons" onClick={(e) => e.stopPropagation()}>
              {isPinned && (
                <Tooltip title="Unpin row">
                  <button
                    className="mrl-table-unpin-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUnpinnedRows((prev) => new Set([...prev, record.id]));
                    }}
                  >
                    📌
                  </button>
                </Tooltip>
              )}
              <Tooltip title={record.status === "Approved" ? "Unapprove" : "Approve"}>
                <button className={`mrl-table-approve-btn ${record.status === "Approved" ? "approved" : ""}`} onClick={(e) => openApproveModal(record.id, e)}>
                  {record.status === "Approved" ? <FaTimes /> : <FaCheck />}
                </button>
              </Tooltip>
              <Tooltip title="View">
                <button className="mrl-table-view-btn" onClick={() => navigate(`/memberDetails/${record.id}`)}>
                  <FaEye />
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button className="mrl-table-delete-btn" disabled>
                  <FaTrash />
                </button>
              </Tooltip>
            </div>
          );
        },
        width: 200
      }
    ],
    [currentPage, pageSize, sortConfig, openApproveModal, navigate, handleRoleAssign, availableRoles, roleAssignLoading, userUnreadActivityCounts, isFlaggedRow, unpinnedRows]
  );

  // Main useEffect to fetch members and unread counts
  useEffect(() => {
    let isMounted = true;

    const fetchMembers = async () => {
      dispatch({ type: "setLoading", payload: true });

      try {
        const token = await getAuthToken();

        const response = await axios.get(API_ENDPOINTS.MEMBERS, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          // Map users to add isNew, isActive, isFormer flags if needed
          const users = (response.data.users || []).map((user) => ({
            ...user,
            isNew: user.isNew || false,
            isActive: user.isActive || true,
            isFormer: user.isFormer || false,
            status: user.status || "Pending"
          }));

          dispatch({
            type: "setUsers",
            payload: users
          });

          // Hide the loading spinner immediately once data is ready
          dispatch({ type: "setLoading", payload: false });

          // Fetch unread counts in the background (does not block UI)
          fetchUnreadCounts();
        }
      } catch (error) {
        console.error("Error fetching members:", error);
        if (isMounted) {
          showNotification("Failed to load members", "error");
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchMembers();

    return () => {
      isMounted = false;
    };
  }, [fetchUnreadCounts, showNotification]);

  // Refresh unread counts when the page gets focus (user returns from member details)
  useEffect(() => {
    const handleFocus = () => {
      console.log("[MemberList] Window focused, refreshing unread counts");
      refreshUnreadCounts();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [refreshUnreadCounts]);

  // Optional: Refresh unread counts every 30 seconds
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log("[MemberList] Auto-refreshing unread counts");
      refreshUnreadCounts();
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [refreshUnreadCounts]);

  // Custom row component — wraps flagged rows in a Tooltip
  const FLAGGED_ROW_TOOLTIP = "This user has been assigned a non-user role before approving membership form.";

  const tableComponents = useMemo(
    () => ({
      body: {
        row: ({ "data-flagged": flagged, ...restProps }) => {
          const row = <tr {...restProps} />;
          if (flagged === "true") {
            return (
              <Tooltip title={FLAGGED_ROW_TOOLTIP} placement="top" mouseEnterDelay={0.3}>
                {row}
              </Tooltip>
            );
          }
          return row;
        }
      }
    }),
    []
  );

  return (
    <Layout
      navTitle="Member Management"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search across all fields..."
            searchTerm={displaySearchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="member-list"
            excelSheetName="Members"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id}
          rowClassName={(record, index) => {
            const flagged = isFlaggedRow(record);
            if (flagged) return "table-row-flagged";
            return index % 2 === 0 ? "table-row-light" : "table-row-dark";
          }}
          components={tableComponents}
          onRow={(record) => ({
            "data-flagged": isFlaggedRow(record) ? "true" : "false"
          })}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
          onChange={handleTableChange}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      <Modal
        title="Select Date Range"
        open={showDateRangeModal}
        onCancel={handleDateRangeModalCancel}
        footer={[
          <Button key="cancel" onClick={handleDateRangeModalCancel}>
            Cancel
          </Button>,
          <Button key="apply" onClick={applyDateRangeFilter}>
            Apply Filter
          </Button>
        ]}
        width={450}
        centered={false}
        style={{ top: 120 }}
      >
        <div style={{ marginBottom: "20px" }}>
          <RangePicker
            style={{ width: "100%" }}
            value={tempFromDate && tempToDate ? [dayjs(tempFromDate), dayjs(tempToDate)] : []}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
            placeholder={["From Date", "To Date"]}
          />
        </div>
      </Modal>

      <Modal
        title={users.find((u) => u.id === selectedMemberId)?.status === "Approved" ? "Remove Approval" : "Confirm Approval"}
        open={showApproveModal}
        onCancel={() => dispatch({ type: "setApproveModal", payload: { show: false } })}
        footer={[
          <Button className="mrl-modal-cancel-btn" key="cancel" onClick={() => dispatch({ type: "setApproveModal", payload: { show: false } })}>
            Cancel
          </Button>,
          <Button className="mrl-modal-confirm-btn" key="confirm" loading={isApproving} onClick={confirmApprove}>
            Confirm
          </Button>
        ]}
        centered
      >
        <p>{users.find((u) => u.id === selectedMemberId)?.status === "Approved" ? "Do you want to unapprove this member?" : "Are you sure you want to approve this member?"}</p>
      </Modal>
    </Layout>
  );
}

export default MemberList;